import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { fmtCr, fmtDay, PROGRESS } from "@/lib/api";

// The five integrated plants first, then the central units, then the groups of mines.
const ORDER = ["DSP", "BSL", "BSP", "ISP", "RSP", "CMO", "L&I", "SSO", "DML", "Central-Others",
               "CGOM", "JGOM", "OGOM"];
const rank = (code) => (ORDER.indexOf(code) + 1 || 99);
const slug = (text = "") => text.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "all";

const Num = ({ value, label, colour }) => (
  <span className="w-[88px] shrink-0 text-right">
    <span
      className="font-data block text-[13px] font-bold tabular-nums"
      style={colour ? { color: colour } : undefined}
    >
      {value}
    </span>
    <span className="font-data block text-[8.5px] uppercase tracking-widest text-muted-foreground">
      {label}
    </span>
  </span>
);

/**
 * The unit-by-unit layer — a supporting band, not a section that shouts. One line each, the
 * detail only when it is asked for, and every figure is of the financial year picked above.
 */
export const UnitLayer = ({ units = [], groups = [], fy }) => {
  const [open, setOpen] = useState("");
  const blocks = groups.length ? groups : [{ group: "", units: null }];

  return (
    <section data-testid="subtle-unit-summary-layer">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="label-caps">Unit by unit · {fy}</div>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">
            How each unit is faring
          </h2>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Press a unit for its register, its compliance and what needs a push.
        </p>
      </div>

      <div className="mt-2 space-y-5">
        {blocks.map((block) => {
          const rows = units
            .filter((u) => !block.units || block.units.includes(u.code))
            .sort((a, b) => rank(a.code) - rank(b.code));
          if (!rows.length) return null;
          const cases = rows.reduce((s, u) => s + (u.total || 0), 0);
          const value = rows.reduce((s, u) => s + (u.expected_cr || 0), 0);
          const watched = rows.reduce((s, u) => s + (u.watched || 0), 0);
          return (
            <div key={block.group || "all"} data-testid={`unit-group-${slug(block.group)}`}>
              {block.group && (
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border pb-1">
                  <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
                    {block.group}
                  </span>
                  <span className="font-data text-[10px] text-muted-foreground">
                    {rows.length} unit(s) · {cases} usecase(s) · {fmtCr(value)}
                    {watched ? ` · ${watched} on compliance watch` : ""}
                  </span>
                </div>
              )}
              <div className="divide-y divide-border border-x border-b border-border bg-card">
                {rows.map((u) => {
                  const on = open === u.code;
                  return (
                    <div key={u.code} data-testid={`unit-line-${u.code}`}>
                      <button
                        type="button"
                        data-testid={`unit-line-toggle-${u.code}`}
                        aria-expanded={on}
                        onClick={() => setOpen(on ? "" : u.code)}
                        className={`flex w-full items-center gap-3 border-l-2 py-2.5 pl-4 pr-4 text-left transition-colors duration-200 hover:bg-muted ${
                          on ? "border-primary bg-muted" : "border-transparent"
                        }`}
                      >
                        <ChevronRight
                          className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-200 ${
                            on ? "rotate-90" : ""
                          }`}
                        />
                        <span className="w-[108px] shrink-0">
                          <span className="block font-display text-[13px] font-black tracking-tight">
                            {u.code}
                          </span>
                          <span className="block truncate text-[9px] text-muted-foreground">
                            {u.name}
                          </span>
                        </span>
                        <span className="min-w-0 flex-1" />
                        <Num value={u.total} label="usecases" />
                        <Num
                          value={u.expected_cr ? u.expected_cr.toFixed(1) : "—"}
                          label="expected ₹Cr"
                          colour="hsl(var(--primary))"
                        />
                        <Num
                          value={u.vetted_cr ? u.vetted_cr.toFixed(1) : "—"}
                          label="vetted ₹Cr"
                          colour={u.vetted_cr ? "hsl(var(--healthy))" : undefined}
                        />
                        <Num
                          value={u.cumulative_pct === null ? "—" : `${u.cumulative_pct}%`}
                          label="compliance"
                          colour={u.cumulative_pct !== null ? "hsl(var(--watch))" : undefined}
                        />
                        <Num
                          value={u.needs_push}
                          label="to fix"
                          colour={u.needs_push ? PROGRESS.needs_attention.color : undefined}
                        />
                      </button>

                      <AnimatePresence initial={false}>
                        {on && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            className="overflow-hidden"
                          >
                            <div
                              className="flex flex-wrap items-center gap-x-8 gap-y-2 border-t border-border bg-muted/40 px-11 py-3 text-[11px]"
                              data-testid={`unit-line-detail-${u.code}`}
                            >
                              <span className="text-muted-foreground">
                                Identified{" "}
                                <b className="font-data text-foreground">{u.identified ?? 0}</b>
                              </span>
                              <span className="text-muted-foreground">
                                Ongoing <b className="font-data text-foreground">{u.ongoing}</b>
                              </span>
                              <span className="text-muted-foreground">
                                Completed{" "}
                                <b className="font-data text-foreground">{u.completed}</b>
                              </span>
                              <span className="text-muted-foreground">
                                Running late{" "}
                                <b className="font-data text-foreground">{u.delayed ?? 0}</b>
                              </span>
                              <span className="text-muted-foreground">
                                On compliance watch{" "}
                                <b className="font-data text-foreground">{u.watched ?? 0}</b>
                                {u.reading_days ? ` · ${u.reading_days} day(s) filled` : ""}
                              </span>
                              <span className="text-muted-foreground">
                                Impact recorded{" "}
                                <b className="font-data text-foreground">
                                  {u.impact_lakh ? `₹${u.impact_lakh} L` : "—"}
                                </b>
                              </span>
                              <span className="text-muted-foreground">
                                Data as on{" "}
                                <b className="font-data text-foreground">
                                  {u.last_updated_on || "—"}
                                </b>
                              </span>
                              <Link
                                to={`/projects?plant=${u.code}`}
                                data-testid={`unit-line-open-${u.code}`}
                                className="border border-primary px-2.5 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-primary transition-colors duration-200 hover:bg-primary hover:text-primary-foreground"
                              >
                                Open the {u.code} register
                              </Link>
                              {u.watched > 0 && (
                                <Link
                                  to={`/compliance-data?plant=${u.code}`}
                                  data-testid={`unit-line-compliance-${u.code}`}
                                  className="text-primary underline-offset-2 hover:underline"
                                >
                                  See its compliance data
                                </Link>
                              )}
                                {u.needs_push > 0 && (
                                <Link
                                  to={`/inconsistency?plant=${u.code}`}
                                  className="text-[hsl(var(--watch))] underline-offset-2 hover:underline"
                                >
                                  See what needs a push
                                </Link>
                              )}
                            </div>
                            {!!(u.meetings || []).length && (
                              <div
                                className="border-t border-border bg-muted/20 px-11 py-3"
                                data-testid={`unit-meetings-${u.code}`}
                              >
                                <div className="label-caps">
                                  Review cadence · last held and next planned
                                </div>
                                <div className="mt-1.5 grid gap-x-8 gap-y-1 sm:grid-cols-2">
                                  {u.meetings.map((m) => (
                                    <div
                                      key={m.kind}
                                      className="flex flex-wrap items-baseline gap-x-2 text-[11px]"
                                      data-testid={`unit-meeting-${u.code}-${slug(m.kind)}`}
                                    >
                                      <span className="font-display text-[10px] font-bold uppercase tracking-widest">
                                        {m.kind}
                                      </span>
                                      <span className="text-muted-foreground">
                                        last{" "}
                                        <b className="font-data text-foreground">
                                          {m.last_date ? fmtDay(m.last_date) : "none yet"}
                                        </b>
                                        {m.last_date && m.last_minutes ? " · minutes kept" : ""}
                                      </span>
                                      <span className="text-muted-foreground">
                                        · next{" "}
                                        <b
                                          className="font-data"
                                          style={{
                                            color: m.next_date
                                              ? "hsl(var(--primary))"
                                              : "hsl(var(--watch))",
                                          }}
                                        >
                                          {m.next_date
                                            ? `${fmtDay(m.next_date)}${
                                                m.next_label ? ` — ${m.next_label}` : ""
                                              }`
                                            : "nothing scheduled"}
                                        </b>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
